import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  BaseButton,
  parsePolicyText,
  sectionsToPolicyText,
  useT,
} from '@repo/common';
import PolicyPage from '../../../pages/Policy/PolicyPage';
import {
  formatPolicyDate,
  policyDateLabel,
  policyLocale,
} from '../../../pages/Policy/policyRegistry';
import { POLICY_LANGS } from '../../../pages/Policy/usePolicyVersions';
import {
  createPolicyVersion,
  getAdminPolicyVersion,
  updatePolicyVersion,
} from '../../../api/policy/policy.api';

// 정책 문서 새 버전 작성·수정 — 국문·영문 본문과 실제 페이지 미리보기
const INPUT_CLASS =
  'w-full h-[40px] rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 px-[12px] typo-b14 text-text-primary placeholder:text-text-placeholder focus:outline-none focus:border-grayscale-gray04 disabled:bg-grayscale-gray01';

const LANG_LABELS = { ko: '한국어', en: 'English' };

const emptyTexts = () => ({ ko: { title: '', body: '' }, en: { title: '', body: '' } });

const daysBetween = (fromIso, toIso) =>
  Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86400000);

const errorText = (error, fallback) =>
  error?.response?.data?.msg || error?.response?.data?.message || fallback;

const fromResponse = (data) => ({
  ko: { title: data.titleKo || '', body: data.bodyKo || '' },
  en: { title: data.titleEn || '', body: data.bodyEn || '' },
});

const currentTexts = async (current) => {
  if (!current) return emptyTexts();
  if (current.source === 'admin') return fromResponse(await getAdminPolicyVersion(current.id));

  const texts = emptyTexts();
  POLICY_LANGS.forEach((lang) => {
    const document = current.languages[lang]?.document;
    if (document) {
      texts[lang] = { title: document.title, body: sectionsToPolicyText(document.sections) };
    }
  });
  return texts;
};

const PolicyVersionEditor = ({ policyKey, merged, editing, onCancel, onSaved }) => {
  const t = useT();
  const [effectiveDate, setEffectiveDate] = useState('');
  const [texts, setTexts] = useState(emptyTexts);
  const [activeLang, setActiveLang] = useState(policyLocale(policyKey));
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const deferredBody = useDeferredValue(texts[activeLang].body);
  const sections = useMemo(() => parsePolicyText(deferredBody), [deferredBody]);

  const isEdit = editing.mode === 'edit';
  const active = texts[activeLang];

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    const load = isEdit
      ? getAdminPolicyVersion(editing.version.id).then((data) => ({
          effectiveDate: data.effectiveDate,
          texts: fromResponse(data),
        }))
      : currentTexts(merged.current).then((loaded) => ({ effectiveDate: '', texts: loaded }));

    load
      .then((data) => {
        if (cancelled) return;
        setEffectiveDate(data.effectiveDate);
        setTexts(data.texts);
        setReady(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(errorText(e, t('Could not load the version.', '버전을 불러오지 못했어요.')));
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyKey, editing]);

  const updateActive = (patch) =>
    setTexts((prev) => ({ ...prev, [activeLang]: { ...prev[activeLang], ...patch } }));

  const reloadCurrent = async () => {
    const touched = POLICY_LANGS.some((lang) => texts[lang].body.trim());
    if (
      touched &&
      !window.confirm(
        t(
          'Replace both languages with the current version?',
          '지금 쓴 국문·영문 내용을 현재 시행 중인 버전으로 바꿀까요?',
        ),
      )
    ) {
      return;
    }
    try {
      setTexts(await currentTexts(merged.current));
    } catch (e) {
      setError(errorText(e, t('Could not load the version.', '버전을 불러오지 못했어요.')));
    }
  };

  const takenDates = new Set(
    merged.all
      .filter((version) => !(isEdit && version.id === editing.version.id))
      .map((version) => version.effectiveDate),
  );
  const gap = effectiveDate ? daysBetween(merged.today, effectiveDate) : null;
  const filled = POLICY_LANGS.filter((lang) => texts[lang].title.trim() || texts[lang].body.trim());

  const problems = [];
  if (!effectiveDate) problems.push(t('Pick the effective date.', '시행일을 골라 주세요.'));
  else if (gap < 1) problems.push(t('The effective date must be after today.', '시행일은 내일 이후여야 해요.'));
  else if (takenDates.has(effectiveDate)) {
    problems.push(t('A version with this date already exists.', '같은 시행일의 버전이 이미 있어요.'));
  }
  if (filled.length === 0) {
    problems.push(t('Write at least one language.', '국문·영문 중 하나 이상 작성해 주세요.'));
  }
  filled.forEach((lang) => {
    if (!texts[lang].title.trim() || !texts[lang].body.trim()) {
      problems.push(
        t(
          `${LANG_LABELS[lang]}: enter both the title and the body.`,
          `${LANG_LABELS[lang]}: 제목과 본문을 모두 입력해 주세요.`,
        ),
      );
    }
  });
  const missingLang = filled.length === 1 ? POLICY_LANGS.find((lang) => lang !== filled[0]) : null;

  const handleSave = async () => {
    if (problems.length) return;
    setSaving(true);
    setError(null);
    const pick = (lang, key) => (filled.includes(lang) ? texts[lang][key].trim() || null : null);
    const payload = {
      effectiveDate,
      titleKo: pick('ko', 'title'),
      bodyKo: filled.includes('ko') ? texts.ko.body : null,
      titleEn: pick('en', 'title'),
      bodyEn: filled.includes('en') ? texts.en.body : null,
    };
    try {
      const saved = isEdit
        ? await updatePolicyVersion(editing.version.id, payload)
        : await createPolicyVersion(policyKey, payload);
      onSaved(saved);
    } catch (e) {
      setError(errorText(e, t('Could not save the version.', '버전을 저장하지 못했어요.')));
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <p className='typo-b14 text-text-placeholder'>{t('Loading…', '불러오는 중…')}</p>
    );
  }

  return (
    <section className='flex flex-col gap-[16px] rounded-[16px] border border-grayscale-gray02 bg-grayscale-gray00 p-[20px] tabletH:p-[24px]'>
      <div className='flex flex-wrap items-center justify-between gap-[8px]'>
        <h3 className='typo-b16Strong text-text-primary'>
          {isEdit
            ? t('Edit scheduled version', '시행 예정 버전 수정')
            : t('Register a new version', '새 버전 등록')}
        </h3>
        <BaseButton type='text_basic' size='s' hideIcon onClick={reloadCurrent}>
          {t('Start from the current version', '현재 버전 내용 불러오기')}
        </BaseButton>
      </div>

      <label className='flex flex-col gap-[6px] min-w-0 tabletH:max-w-[320px]'>
        <span className='typo-b13Strong text-text-secondary'>{t('Effective date', '시행일')}</span>
        <input
          type='date'
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          className={INPUT_CLASS}
        />
        {effectiveDate && (
          <span className='typo-b12 text-text-placeholder'>
            {formatPolicyDate(effectiveDate, 'ko')} · {formatPolicyDate(effectiveDate, 'en')}
            {gap > 0 && ` · D-${gap}`}
          </span>
        )}
      </label>

      {gap !== null && gap >= 1 && gap < 7 && (
        <div className='rounded-[10px] bg-functional-warning01 px-[14px] py-[10px] typo-b13 text-text-primary'>
          {t(
            `Only ${gap} day(s) until it takes effect. The notice must go out at least 7 days ahead (30 days if the change is unfavorable to users).`,
            `시행일까지 ${gap}일밖에 없어요. 개정 공지는 시행 7일 전(이용자에게 불리한 변경은 30일 전)까지 나가야 해요.`,
          )}
        </div>
      )}

      <div className='flex flex-wrap items-center gap-[12px]'>
        <div role='tablist' className='flex rounded-[10px] bg-grayscale-gray01 p-[3px]'>
          {POLICY_LANGS.map((lang) => (
            <button
              key={lang}
              type='button'
              role='tab'
              aria-selected={activeLang === lang}
              onClick={() => setActiveLang(lang)}
              className={`flex items-center gap-[6px] rounded-[8px] px-[14px] py-[6px] typo-b13Strong ${
                activeLang === lang
                  ? 'bg-grayscale-gray00 text-text-primary shadow-4'
                  : 'text-text-placeholder hover:text-text-secondary'
              }`}
            >
              {LANG_LABELS[lang]}
              {filled.includes(lang) && (
                <span className='size-[6px] rounded-full bg-functional-success03' />
              )}
            </button>
          ))}
        </div>
        {missingLang && (
          <span className='typo-b12 text-text-placeholder'>
            {t(
              `${LANG_LABELS[missingLang]} is empty — this version will show in ${LANG_LABELS[filled[0]]} only.`,
              `${LANG_LABELS[missingLang]}가 비어 있어요. 이 버전은 ${LANG_LABELS[filled[0]]}로만 보여요.`,
            )}
          </span>
        )}
      </div>

      <label className='flex flex-col gap-[6px] min-w-0'>
        <span className='typo-b13Strong text-text-secondary'>
          {t('Title', '제목')} · {LANG_LABELS[activeLang]}
        </span>
        <input
          type='text'
          value={active.title}
          onChange={(e) => updateActive({ title: e.target.value })}
          placeholder={activeLang === 'ko' ? '이용약관' : 'Terms of Use'}
          maxLength={200}
          className={INPUT_CLASS}
        />
      </label>

      <details className='rounded-[10px] bg-grayscale-gray01 px-[14px] py-[10px] typo-b13 text-text-secondary'>
        <summary className='cursor-pointer typo-b13Strong'>
          {t('Writing rules', '작성 규칙')}
        </summary>
        <ul className='mt-[8px] flex flex-col gap-[2px]'>
          <li>
            <code># 장 제목</code> — {t('chapter (e.g. Chapter 1)', '장 (예: 제1장 총칙)')}
          </li>
          <li>
            <code>## 조항 제목</code> — {t('article heading', '조항 제목 (예: 제1조 (목적))')}
          </li>
          <li>{t('One line = one paragraph. A blank line adds space.', '한 줄이 한 문단이에요. 빈 줄은 문단 사이 여백이 돼요.')}</li>
          <li>
            <code>| 항목 | 내용 |</code> — {t('table rows (first row is the header)', '표 (첫 줄이 머리글, 칸 안 줄바꿈은 <br>)')}
          </li>
          <li>
            <code>[글자](https://…)</code> — {t('link. Plain URLs and emails become links too.', '링크. 주소·이메일은 그대로 써도 링크가 돼요.')}
          </li>
        </ul>
      </details>

      <div className='grid gap-[16px] desktopL:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'>
        <textarea
          value={active.body}
          onChange={(e) => updateActive({ body: e.target.value })}
          spellCheck={false}
          placeholder={t(`${LANG_LABELS[activeLang]} body`, `${LANG_LABELS[activeLang]} 본문`)}
          className='w-full h-[480px] desktopL:h-[720px] rounded-[10px] border border-grayscale-gray02 bg-grayscale-gray00 px-[14px] py-[12px] font-mono text-[13px] leading-[1.6] text-text-primary focus:outline-none focus:border-grayscale-gray04 resize-y'
        />
        <div className='h-[480px] desktopL:h-[720px] overflow-y-auto rounded-[10px] border border-grayscale-gray02'>
          <PolicyPage
            title={active.title}
            lastUpdated={effectiveDate ? formatPolicyDate(effectiveDate, activeLang) : '—'}
            lastUpdatedLabel={policyDateLabel(activeLang, true)}
            sections={sections}
          />
        </div>
      </div>

      {(problems.length > 0 || error) && (
        <div className='rounded-[10px] bg-functional-failed01 px-[14px] py-[10px] typo-b13 text-functional-failed03'>
          <ul className='flex flex-col gap-[2px]'>
            {error && <li>· {error}</li>}
            {problems.map((problem) => (
              <li key={problem}>· {problem}</li>
            ))}
          </ul>
        </div>
      )}

      <div className='flex flex-wrap justify-end gap-[8px]'>
        <BaseButton type='text_basic' size='m' hideIcon onClick={onCancel} isDisabled={saving}>
          {t('Cancel', '취소')}
        </BaseButton>
        <BaseButton
          type='primary'
          size='m'
          hideIcon
          onClick={handleSave}
          isLoading={saving}
          isDisabled={saving || problems.length > 0}
        >
          {isEdit ? t('Save changes', '수정 저장') : t('Register version', '버전 등록')}
        </BaseButton>
      </div>
    </section>
  );
};

export default PolicyVersionEditor;
